import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IApplicationState } from "../../redux/reducers";
import {
	Table,
	Modal,
	ModalHeader,
	ModalFooter,
	ModalBody,
	Form,
	FormGroup,
	Label,
	Input,
	InputGroup,
	InputGroupAddon
} from "reactstrap";
import Button from "../../components/common/button";
import { Upload, Download, Plus, X, FileText, Loader } from "react-feather";
import ReportRow from "./components/reportRow";
import { IReport } from "../../interfaces/report";
import { ICompany } from "../../interfaces/company";
import reportService from "../../services/reportService";
import companyService from "../../services/companyService";
import { error } from "../../utils/notification";
import { REPORTS_ALLOWED_FILE_EXTENSIONS } from "../../utils/constants";
import adminService from "../../services/adminService";
import {
	paginateAdminReportRequests,
	paginateAdminReports
} from "../../redux/actions/admin/adminActions";
import { IPagination } from "../../interfaces/pagination";
import Pagination from "../../components/pagination";

interface IProps {
	reports: IReport[];
	requests: IReport[];
	companies: ICompany[];
	getAdminReportRequests: () => Promise<any>;
	paginateReportRequests: (page: number) => void;
	paginationRequests: IPagination;
	getAdminReports: () => Promise<any>;
	paginateReports: (page: number) => void;
	paginationReports: IPagination;
	uploadReportFile: (reportId: string, reportFile: File) => Promise<any>;
	getCompanies: () => any;
	addReport: (
		reportType: string,
		reportFile: File,
		reportDescription: string,
		requestedBy: string
	) => Promise<any>;
}

enum SelectedTypes {
	reports = "Reports",
	requests = "Requests"
}

const ReportsTable: React.FC<IProps> = ({
	reports,
	requests,
	companies,
	getAdminReports,
	paginateReports,
	paginationReports,
	getAdminReportRequests,
	paginateReportRequests,
	paginationRequests,
	uploadReportFile,
	getCompanies,
	addReport
}) => {
	const [modalNewReport, setModalNewReport] = useState<boolean>(false);
	const [reportType, setReportType] = useState<string>();
	const [reportDescription, setReportDescription] = useState<string>();
	const [reportRequestedBy, setRequestedBy] = useState<string>();
	const [reportNewFile, setReportNewFile] = useState<File>();
	const [adding, setAdding] = useState<boolean>(false);

	const [modal, setModal] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [uploading, setUploading] = useState<boolean>(false);
	const [report, setReport] = useState<IReport>();
	const [reportFile, setReportFile] = useState<File>();

	const [selected, setSelected] = useState<SelectedTypes>(SelectedTypes.requests);

	function toggleModal() {
		setModal(!modal);
	}

	function selectReport(report: IReport) {
		setReport(report);
		setReportFile(undefined);
		setModal(true);
	}

	function selectReportFile(event: any) {
		setReportFile(event.target.files[0]);
	}

	function selectReportNewFile(event: any) {
		setReportNewFile(event?.target.files[0]);
	}

	function uploadReport() {
		if (report === undefined || reportFile === undefined) return;

		setUploading(true);

		uploadReportFile(report.id, reportFile).finally(() => {
			getAdminReportRequests().finally(() => {
				setUploading(false);
				setModal(false);
			});
		});
	}

	function createNewReport() {
		if (
			reportType === undefined ||
			reportDescription === undefined ||
			reportNewFile === undefined ||
			reportRequestedBy === undefined
		) {
			error("Invalid Report params");
			return;
		} else {
			setAdding(true);
			addReport(reportType, reportNewFile, reportDescription, reportRequestedBy).finally(() => {
				setAdding(false);
				setModalNewReport(false);
			});
		}
	}

	async function onPageRequestsChanged(page: number) {
		await paginateReportRequests(page);
		setLoading(true);
		getAdminReportRequests().finally(() => {
			setLoading(false);
		});
	}

	async function onPageReportsChanged(page: number) {
		await paginateReports(page);
		setLoading(true);
		getAdminReports().finally(() => {
			setLoading(false);
		});
	}

	useEffect(() => {
		setLoading(true);
		Promise.all([getCompanies(), getAdminReports(), getAdminReportRequests()]).finally(() => {
			setLoading(false);
		});
	}, [paginationReports.page]);

	return (
		<div className="user-reports">
			{/* HEADER */}
			<div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 flex-wrap">
				<h3 className="mb-2 mb-md-0">
					All {SelectedTypes.reports === selected ? SelectedTypes.reports : SelectedTypes.requests}
				</h3>

				<div className="d-flex flex-wrap align-items-center gap-2">
					<Button
						color={selected === SelectedTypes.requests ? "primary" : "white"}
						onClick={() => setSelected(SelectedTypes.requests)}
					>
						<Upload size={16} className="mr-1" /> Requests
					</Button>
					<Button
						color={selected === SelectedTypes.reports ? "primary" : "white"}
						onClick={() => setSelected(SelectedTypes.reports)}
					>
						<Download size={16} className="mr-1" /> Reports
					</Button>
				</div>

				{((selected === SelectedTypes.requests ? paginationRequests.pages : paginationReports.pages) > 1) && (
					<div className="admin-data-pagination">
						<div className="admin-data-pagination__controls">
							<button
								type="button"
								className="admin-data-pagination__button btn-prev"
								disabled={(selected === SelectedTypes.requests ? paginationRequests.page : paginationReports.page) === 1}
								onClick={() => {
									if (selected === SelectedTypes.requests) {
										onPageRequestsChanged(Math.max(1, paginationRequests.page - 1));
									} else {
										onPageReportsChanged(Math.max(1, paginationReports.page - 1));
									}
								}}
							>
								<i className="fas fa-chevron-left"></i>
								Previous
							</button>
						</div>
						<div className="admin-data-pagination__info">
							<div className="page-info">
								Page {selected === SelectedTypes.requests ? paginationRequests.page : paginationReports.page} of {selected === SelectedTypes.requests ? paginationRequests.pages : paginationReports.pages}
							</div>
							<div className="results-info">
								{selected === SelectedTypes.requests ? paginationRequests.total : paginationReports.total} {selected.toLowerCase()}
							</div>
						</div>
						<div className="admin-data-pagination__next-controls">
							<button
								type="button"
								className="admin-data-pagination__button btn-next"
								disabled={(selected === SelectedTypes.requests ? paginationRequests.page : paginationReports.page) === (selected === SelectedTypes.requests ? paginationRequests.pages : paginationReports.pages)}
								onClick={() => {
									if (selected === SelectedTypes.requests) {
										onPageRequestsChanged(Math.min(paginationRequests.pages, paginationRequests.page + 1));
									} else {
										onPageReportsChanged(Math.min(paginationReports.pages, paginationReports.page + 1));
									}
								}}
							>
								Next
								<i className="fas fa-chevron-right"></i>
							</button>
						</div>
					</div>
				)}
			</div>

			{/* TABLE */}
			<div className="admin-data-table-wrapper">
				<div className="table-responsive">
					<Table className="admin-data-table reports-table">
						<thead>
							<tr className="table-header">
								<th>Report Type</th>
								<th>Request Date</th>
								<th>Requested By</th>
								<th className="actions-column">Actions</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={4} className="loading-cell">
										<div className="loading-spinner">
											<div className="spinner"></div>
											<span>Loading reports...</span>
										</div>
									</td>
								</tr>
							) : (selected === SelectedTypes.requests ? requests : reports).length === 0 ? (
								<tr>
									<td colSpan={4} className="empty-cell">
										<div className="empty-state">
											<div className="empty-icon">📊</div>
											<span>No {selected.toLowerCase()} found</span>
										</div>
									</td>
								</tr>
							) : (
								(selected === SelectedTypes.requests ? requests : reports).map((report) => (
									<tr key={report.id} className="table-row clickable" onClick={() => selectReport(report)}>
										<td className="cell-name">{report.report_type || "N/A"}</td>
										<td>{new Date(report.request_date).toLocaleDateString()}</td>
										<td>{report.requested_by?.name || "N/A"}</td>
										<td className="actions-cell">
											<button
												type="button"
												className="action-btn btn-view"
												onClick={(e) => {
													e.stopPropagation();
													selectReport(report);
												}}
											>
												View
											</button>
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</div>
			</div>

			{/* FLOATING ADD BUTTON */}
			<span className="position-fixed bottom-0 end-0 m-4">
				<Button
					className="btn-circle btn-adder btn-secondary shadow-lg"
					onClick={() => setModalNewReport(true)}
				>
					<Plus size={24} />
				</Button>
			</span>

			{/* UPLOAD MODALS */}
			<Modal
				isOpen={modal}
				size="lg"
				className="w-100"
				style={{ maxWidth: "95%", margin: "0 auto" }}
			>
				<ModalHeader toggle={toggleModal}>
					<Upload className="color-bg-blue mr-2" size={24} />
					Upload New Report For {report?.requested_by.name}
				</ModalHeader>
				<ModalBody>
					<Form onSubmit={uploadReport}>
						<FormGroup className="mb-3">
							<Label>Report Type</Label>
							<Input
								placeholder="Report Type"
								value={report?.report_type}
								disabled
								style={{ width: "100%", padding: "8px" }}
							/>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Assign Report</Label>
							<Input
								placeholder="Assign Report"
								value={`${report?.requested_by.name}`}
								disabled
								style={{ width: "100%", padding: "8px" }}
							/>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Upload Report</Label>
							<InputGroup>
								<Input
									type="file"
									accept={REPORTS_ALLOWED_FILE_EXTENSIONS.join(", ")}
									onChange={selectReportFile}
									style={{
										padding: "8px",
										borderStyle: "dashed",
										borderColor: "#cfdbea"
									}}
								/>
								<InputGroupAddon addonType="append">
									<Upload
										className="color-bg-blue"
										size={16}
										style={{
											marginLeft: "-29px",
											marginTop: "15px",
											zIndex: 100
										}}
									/>
								</InputGroupAddon>
							</InputGroup>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Description</Label>
							<Input
								type="textarea"
								rows={4}
								value={report?.description}
								disabled
								placeholder="Description"
								style={{ width: "100%", padding: "8px" }}
							/>
						</FormGroup>
					</Form>
				</ModalBody>
				<ModalFooter>
					<Button
						color="primary"
						onClick={uploadReport}
						disabled={uploading || reportFile === undefined}
					>
						<FileText size={18} className="mr-2" />
						{uploading ? "Uploading Report" : "Upload Report"}
					</Button>
				</ModalFooter>
			</Modal>

			{/* NEW REPORT MODAL */}
			<Modal
				isOpen={modalNewReport}
				size="lg"
				className="w-100"
				style={{ maxWidth: "95%", margin: "0 auto" }}
			>
				<ModalHeader toggle={() => setModalNewReport(false)}>
					<Upload className="color-bg-blue mr-2" size={24} />
					Upload New Report
				</ModalHeader>
				<ModalBody>
					<Form>
						<FormGroup className="mb-3">
							<Label>Report Type</Label>
							<Input
								placeholder="Report Type"
								value={reportType}
								onChange={(e) => setReportType(e.target.value)}
							/>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Select Company</Label>
							<Input
								type="select"
								value={reportRequestedBy}
								onChange={(e) => setRequestedBy(e.target.value)}
							>
								<option key={""} value={""}>
									Select
								</option>
								{companies.map((item) => (
									<option key={item.id} value={item.id}>
										{`${item.name} (${item.email})`}
									</option>
								))}
							</Input>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Upload Report</Label>
							<InputGroup>
								<Input
									type="file"
									accept={REPORTS_ALLOWED_FILE_EXTENSIONS.join(", ")}
									onChange={selectReportNewFile}
									style={{
										padding: "8px",
										borderStyle: "dashed",
										borderColor: "#cfdbea"
									}}
								/>
								<InputGroupAddon addonType="append">
									<Upload
										className="color-bg-blue"
										size={16}
										style={{
											marginLeft: "-29px",
											marginTop: "15px",
											zIndex: 100
										}}
									/>
								</InputGroupAddon>
							</InputGroup>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label>Description</Label>
							<Input
								type="textarea"
								rows={4}
								value={reportDescription}
								onChange={(e) => setReportDescription(e.target.value)}
								placeholder="Description"
							/>
						</FormGroup>
					</Form>
				</ModalBody>
				<ModalFooter>
					<Button
						color="primary"
						onClick={createNewReport}
						disabled={adding || reportNewFile === undefined}
					>
						<FileText size={18} className="mr-2" />
						{adding ? "Adding Report" : "Add Report"}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => {
	return {
		reports: state.admin.reports.list,
		requests: state.admin.requests.list,
		paginationRequests: state.admin.requests.pagination,
		paginationReports: state.admin.reports.pagination,
		companies: state.company.companies
	};
};

const mapDispatchToProps = {
	getAdminReportRequests: () => adminService.getAdminReportRequests(),
	paginateReportRequests: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAdminReportRequests(page));
	},
	getAdminReports: () => adminService.getAdminReports(),
	paginateReports: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAdminReports(page));
	},
	uploadReportFile: (reportId: string, reportFile: File) =>
		reportService.uploadReportFile(reportId, reportFile),
	getCompanies: () => companyService.getCompanies(),
	addReport: (
		reportType: string,
		reportFile: File,
		reportDescription: string,
		requestedBy?: string
	) => reportService.addReport(reportType, reportFile, reportDescription, requestedBy)
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportsTable);
